import { IsoDate, QueryBuilder, QueryFilter } from '@softline/core';
import { ApiRow } from '../types/api-row';


export const STATISTICS_PREFIX = 'statistics';

/** The cheap value aggregates. Lagerwert is deliberately NOT here — it is the
    expensive one and gets loaded separately (`loadInventoryValues`). */
export function addValueColumns(builder: QueryBuilder<ApiRow>): void {
  builder.column(`${STATISTICS_PREFIX}.net`, { usage: 'aggregate', agg: 'sum' });
  builder.column(`${STATISTICS_PREFIX}.cost`, { usage: 'aggregate', agg: 'sum' });
  builder.column(`${STATISTICS_PREFIX}.contribution_margin`, {
    usage: 'aggregate',
    agg: 'sum',
  });
}


export function addComparisonColumns(builder: QueryBuilder<ApiRow>): void {
  builder.column(`${STATISTICS_PREFIX}.contribution_margin_percent` as any, {
    usage: 'aggregate',
  });
  builder.column(`${STATISTICS_PREFIX}.net` as any, {
    usage: 'aggregate',
    agg: 'change_pct' as any,
  });
  builder.column(`${STATISTICS_PREFIX}.cost` as any, {
    usage: 'aggregate',
    agg: 'change_pct' as any,
  });
  builder.column(`${STATISTICS_PREFIX}.contribution_margin` as any, {
    usage: 'aggregate',
    agg: 'change_pct' as any,
  });
  builder.column(`${STATISTICS_PREFIX}.contribution_margin_percent` as any, {
    usage: 'aggregate',
    agg: 'change_pct' as any,
  });

  builder.column(`${STATISTICS_PREFIX}.net` as any, {
    usage: 'aggregate',
    agg: 'change_value' as any,
  });
  builder.column(`${STATISTICS_PREFIX}.cost` as any, {
    usage: 'aggregate',
    agg: 'change_value' as any,
  });
  builder.column(`${STATISTICS_PREFIX}.contribution_margin` as any, {
    usage: 'aggregate',
    agg: 'change_value' as any,
  });
}

/** The Lagerwert aggregate on its own. Kept out of the value/comparison column
    sets so the expensive column is only ever asked for when the toggle is on —
    see `loadInventoryValues`. */
export function addInventoryColumns(
  builder: QueryBuilder<ApiRow>,
  options: { withComparison: boolean }
): void {
  builder.column(`${STATISTICS_PREFIX}.inventory_value`, {
    usage: 'aggregate',
    agg: 'sum',
  });
  if (!options.withComparison) return;
  builder.column(`${STATISTICS_PREFIX}.inventory_value` as any, {
    usage: 'aggregate',
    agg: 'change_pct' as any,
  });
  builder.column(`${STATISTICS_PREFIX}.inventory_value` as any, {
    usage: 'aggregate',
    agg: 'change_value' as any,
  });
}

export function addDbPercentColumn(builder: QueryBuilder<ApiRow>): void {
  builder.column(`${STATISTICS_PREFIX}.contribution_margin_percent`, {
    usage: 'aggregate',
  });
}

export function addDateFilter(
  builder: QueryBuilder<ApiRow>,
  from: string,
  to: string
): void {
  builder.filter({
    type: 'dateInRange',
    name: `${STATISTICS_PREFIX}.document_date`,
    valueFrom: from as IsoDate,
    valueTo: to as IsoDate,
  });
}

export function valFromRow(
  row: ApiRow | null | undefined,
  field: string
): number {
  if (!row) return 0;
  const bucket = row[STATISTICS_PREFIX] as Record<string, unknown> | undefined;
  const val = bucket?.[`${field}:sum`] ?? bucket?.[field];
  return (val as number) ?? 0;
}

export function refFromRow(
  row: ApiRow | null | undefined,
  field: string
): number {
  if (!row) return 0;
  const bucket = row[STATISTICS_PREFIX] as Record<string, unknown> | undefined;
  const val = bucket?.[`${field}_reference:sum`] ?? bucket?.[`${field}_reference`];
  return (val as number) ?? 0;
}

export function changePctFromRow(
  row: ApiRow | null | undefined,
  field: string
): number | null {
  if (!row) return null;
  const bucket = row[STATISTICS_PREFIX] as Record<string, unknown> | undefined;
  const val = bucket?.[`${field}:change_pct`];
  return val == null ? null : (val as number);
}

/** Backend-computed absolute change vs. the reference period (EUR). */
export function changeValueFromRow(
  row: ApiRow | null | undefined,
  field: string
): number | null {
  if (!row) return null;
  const bucket = row[STATISTICS_PREFIX] as Record<string, unknown> | undefined;
  const val = bucket?.[`${field}:change_value`];
  return val == null ? null : (val as number);
}

export function addPeriodColumns(builder: QueryBuilder<ApiRow>): void {
  builder.column('query_period', { usage: 'group' });
}

export function addPeriodFilters(
  builder: QueryBuilder<ApiRow>,
  primary: { from: string; to: string },
  reference: { from: string; to: string },
): void {
  const currentFilter: QueryFilter<ApiRow> = {
    type: 'dateInRange',
    name: `${STATISTICS_PREFIX}.document_date`,
    valueFrom: primary.from as IsoDate,
    valueTo: primary.to as IsoDate,
  } as unknown as QueryFilter<ApiRow>;
  const previousFilter: QueryFilter<ApiRow> = {
    type: 'dateInRange',
    name: `${STATISTICS_PREFIX}.document_date`,
    valueFrom: reference.from as IsoDate,
    valueTo: reference.to as IsoDate,
  } as unknown as QueryFilter<ApiRow>;
  builder.filter({
    type: 'conditions',
    operator: 'OR',
    filters: [currentFilter, previousFilter],
  } as unknown as QueryFilter<ApiRow>);
}

export function splitByPeriod(rows: ApiRow[]): {
  current: ApiRow[];
  previous: ApiRow[];
} {
  const current: ApiRow[] = [];
  const previous: ApiRow[] = [];
  for (const row of rows) {
    if (row['query_period'] === 'current') current.push(row);
    else if (row['query_period'] === 'previous') previous.push(row);
  }
  return { current, previous };
}
